import { nhost } from './nhost'
import { signOutSession } from './features/user/signOutSession'

/**
 * Refresh and return a valid access token, shared by every data client
 * (Apollo, tRPC) so they all recover from a dead session the same way.
 *
 * `refreshSession(60)` only hits the auth server when the token is about to
 * expire; on a transient (non-401) error it keeps the existing session, so a
 * flaky network doesn't log the user out. It only returns without an access
 * token when the stored refresh token is truly invalid (rotated / expired /
 * revoked, i.e. a 401), in which case the SDK has already cleared the stored
 * session. In that case we also sign out to revoke it server-side and let the
 * app fall back to the login screen — instead of staying "authenticated" with a
 * token it can never refresh, which otherwise leaves the app stuck until
 * localStorage is cleared by hand.
 */
export const getValidAccessToken = async (): Promise<string | undefined> => {
  const hadStoredSession = !!nhost.sessionStorage.get()

  let session = null
  try {
    session = await nhost?.refreshSession(60)
  } catch (error) {
    console.warn('Session refresh failed', error)
  }

  if (hadStoredSession && !session?.accessToken) {
    try {
      await signOutSession(nhost)
    } catch {
      // Ignore sign-out errors: the local session is cleared regardless.
    }
  }

  return session?.accessToken
}
